import type { PartnerSitzung } from '@nac2s/shared'
import { createContext, type ReactNode, use, useCallback, useEffect, useRef, useState } from 'react'
import { ApiError, beiRechtestand } from '../api/client.js'
import { ladeSitzung } from '../api/partner.js'

/**
 * Takt, in dem die Sitzung nachgelesen wird, solange das Fenster sichtbar ist.
 *
 * Rechte gelten am Endpunkt sofort — die Oberflaeche wusste davon aber erst
 * nach dem naechsten Neuladen. Der Takt schliesst die Luecke fuer ein Fenster,
 * an dem niemand arbeitet; beim Wechsel zurueck ins Fenster wird sofort
 * nachgelesen, und jede Antwort mit neuem Rechtestand loest es ebenfalls aus.
 */
const TAKT_MS = 20_000

/**
 * Hat sich an der Sitzung etwas geaendert? Ohne den Vergleich setzte jeder
 * Takt einen neuen Zustand, und die ganze Oberflaeche baute sich neu auf.
 */
function unveraendert(vorher: Sitzungszustand, jetzt: PartnerSitzung): boolean {
  return vorher.status === 'angemeldet' && JSON.stringify(vorher.sitzung) === JSON.stringify(jetzt)
}

/**
 * Zustand der Anmeldung.
 *
 * `unbekannt` ist der Zustand vor der ersten Antwort und bewusst von
 * `abgemeldet` getrennt: Sonst blitzt beim Laden kurz das Anmeldeformular auf,
 * obwohl die Sitzung gültig ist.
 */
export type Sitzungszustand =
  | { status: 'unbekannt' }
  | { status: 'angemeldet'; sitzung: PartnerSitzung }
  | { status: 'abgemeldet' }
  | { status: 'fehler'; meldung: string }

export interface SitzungContextWert {
  zustand: Sitzungszustand
  neuLaden: () => Promise<void>
}

const SitzungContext = createContext<SitzungContextWert | null>(null)

export function SitzungProvider({ children }: { children: ReactNode }) {
  const [zustand, setZustand] = useState<Sitzungszustand>({ status: 'unbekannt' })

  const neuLaden = useCallback(async () => {
    try {
      setZustand({ status: 'angemeldet', sitzung: await ladeSitzung() })
    } catch (error) {
      if (error instanceof ApiError && error.istNichtAngemeldet) {
        setZustand({ status: 'abgemeldet' })
        return
      }
      setZustand({
        status: 'fehler',
        meldung: error instanceof ApiError ? error.message : 'Unbekannter Fehler',
      })
    }
  }, [])

  /**
   * Stilles Nachlesen: uebernimmt nur ein Ergebnis, das etwas aussagt.
   *
   * Ein Aussetzer des Netzes darf die laufende Oberflaeche nicht in die
   * Fehleransicht werfen — anders als beim ersten Laden. Eine abgelaufene
   * oder entzogene Sitzung (401) wirkt dagegen sofort.
   */
  const stillNachlesen = useCallback(async () => {
    try {
      const sitzung = await ladeSitzung()
      setZustand((vorher) =>
        unveraendert(vorher, sitzung) ? vorher : { status: 'angemeldet', sitzung },
      )
    } catch (error) {
      if (error instanceof ApiError && error.istNichtAngemeldet) {
        setZustand({ status: 'abgemeldet' })
      }
    }
  }, [])

  // Jede Antwort des Dienstes traegt den Rechtestand. Weicht er von dem ab,
  // den diese Sitzung kennt, hat jemand die Rolle geaendert: sofort
  // nachlesen, damit die Aenderung bei der naechsten Handlung wirkt.
  const bekannterStand = useRef<string | null>(null)
  useEffect(
    () =>
      beiRechtestand((stand) => {
        if (bekannterStand.current === stand) return
        const ersterStand = bekannterStand.current === null
        bekannterStand.current = stand
        if (!ersterStand) void stillNachlesen()
      }),
    [stillNachlesen],
  )

  useEffect(() => {
    void neuLaden()
  }, [neuLaden])

  // Nur fuer eine angemeldete Sitzung: Eine offene Anmeldeseite haette sonst
  // im Takt gegen einen Endpunkt geklopft, der sie ohnehin abweist.
  const angemeldet = zustand.status === 'angemeldet'
  useEffect(() => {
    if (!angemeldet) return
    let uhr: ReturnType<typeof setInterval> | null = null

    function anhalten() {
      if (uhr !== null) clearInterval(uhr)
      uhr = null
    }

    function starten() {
      anhalten()
      uhr = setInterval(() => void stillNachlesen(), TAKT_MS)
    }

    function beiRueckkehr() {
      void stillNachlesen()
    }

    function beiSichtbarkeit() {
      if (document.visibilityState === 'visible') {
        void stillNachlesen()
        starten()
      } else {
        anhalten()
      }
    }

    if (document.visibilityState === 'visible') starten()
    document.addEventListener('visibilitychange', beiSichtbarkeit)
    window.addEventListener('focus', beiRueckkehr)
    return () => {
      anhalten()
      document.removeEventListener('visibilitychange', beiSichtbarkeit)
      window.removeEventListener('focus', beiRueckkehr)
    }
  }, [angemeldet, stillNachlesen])

  return <SitzungContext value={{ zustand, neuLaden }}>{children}</SitzungContext>
}

export function useSitzung(): SitzungContextWert {
  const wert = use(SitzungContext)
  if (!wert) throw new Error('useSitzung braucht einen SitzungProvider darüber')
  return wert
}
